import React, { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import Popup from './components/Popup';
import HomePage from './components/HomePage';
import PaymentPage from './components/PaymentPage';
import SuccessPage from './components/SuccessPage';
import FailurePage from './components/FailurePage';
import StatusPage from './components/StatusPage';
import AdminPage from './components/AdminPage';
import HomeLandingPage from './components/HomeLandingPage';
import logger from './utils/logger';
import appConfig from './config/appConfig';
import './styles.css';

// Create a context for popup state management
const PopupContext = createContext();

// Context for admin authentication state
export const AdminAuthContext = createContext();

// Navigation component that uses React Router
const Navigation = () => {
  const navigate = useNavigate();
  const logoSrc = `${appConfig.basePath.replace(/\/$/, '')}/logo.png`;

  return (
    <>
      <nav>
        <div className="nav-logo-container">
          <img src={logoSrc} alt="DUI First Time Offender Program Logo" className="nav-logo" />
        </div>
        <div className="nav-title">
          <h1>DUI First Time Offender Program</h1>
        </div>
        <div className="nav-buttons">
          <button onClick={() => navigate('/')}>Home</button>
          <button onClick={() => navigate('/status')}>Check Status</button>
          <button onClick={() => navigate('/admin')}>Admin</button>
        </div>
      </nav>
    </>
  );
};

// Main content component that conditionally renders navigation
const AppContent = () => {
  const location = useLocation();
  const { instance, accounts } = useMsal();
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [isAgreed, setIsAgreed] = useState(false);
  const paymentResult = new URLSearchParams(location.search).get('paymentResult');

  useEffect(() => {
    logger.info('Page navigation', { 
      path: location.pathname,
      search: location.search 
    });

    if (window.location.hash && window.location.hash.includes('error=')) {
      logger.warn('Clearing auth error hash from URL', {
        hash: window.location.hash
      });
      window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
    }
  }, [location]);

  const handleAgree = () => {
    setIsPopupVisible(false);
    setIsAgreed(true);
    // After agreeing to popup, navigate to form page
    // Use router-aware path (basename is applied automatically)
    window.location.href = `${appConfig.basePath}/form`;
  };

  const showPopup = () => {
    setIsPopupVisible(true);
  };

  const logoutAdmin = async () => {
    await instance.logoutRedirect({
      postLogoutRedirectUri: window.location.origin + appConfig.basePath,
    });
  };

  const activeAccount = instance.getActiveAccount() || accounts[0];
  const isAdminAuthenticated = !!activeAccount;
  const adminUser = activeAccount
    ? { name: activeAccount.name || activeAccount.username, username: activeAccount.username }
    : null;

  // Hide navigation on the landing page only
  const shouldShowNavigation = location.pathname !== '/';

  const rootElement = paymentResult === 'success'
    ? <SuccessPage />
    : paymentResult === 'failure' || paymentResult === 'cancel' || paymentResult === 'duplicate'
      ? <FailurePage />
      : <HomeLandingPage />;

  return (
    <AdminAuthContext.Provider value={{ isAdminAuthenticated, adminUser, logoutAdmin }}>
      <PopupContext.Provider value={{ showPopup, isAgreed }}>
        <div className="App">
          {shouldShowNavigation && <Navigation />}
          <div className="main-content">
            <Routes>
              <Route path="/" element={rootElement} />
              <Route path="/form" element={<HomePage />} />
              <Route path="/payment" element={<PaymentPage />} />
              <Route path="/success" element={<SuccessPage />} />
              <Route path="/failure" element={<FailurePage />} />
              <Route path="/status" element={<StatusPage />} />
              <Route path="/admin" element={<AdminPage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            {isPopupVisible && <Popup onAgree={handleAgree} />}
          </div>
        </div>
      </PopupContext.Provider>
    </AdminAuthContext.Provider>
  );
};

function App() {
  return (
    <Router basename={appConfig.basePath}>
      <AppContent />
    </Router>
  );
}

// Export the context for use in other components
export const usePopup = () => useContext(PopupContext);

export default App;